import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import FormLayout from '../common/FormLayout.vue'
import { ContextualizedFormResolver } from '@/utils/forms/contextualized-form-resolver'
import { FormContext } from '@/utils/forms/form-context'
import { cloneDeep, isEmpty, isEqual } from 'lodash'
import { resolveFormDataForValidation } from '@/utils/forms/form-resolver'
import { validateFormData } from '@/utils/forms/form-validate'
import CurrentUserContext from '@/models/current-user-context'
import { updateLocationDetails } from '@/api/account-data/manage-locations'
import { useGetLocationStatusesQuery } from '@/api/locations'

interface LocationTypeCustomFieldsSchema {
  schema?: {
    properties: object
  }
  uiSchema?: object
}
interface Props {
  statusId?: number | null
  description?: string | null
  name: string
  locationId: number | string
  rootSchema: LocationTypeCustomFieldsSchema | null
  customData: object | null
}

interface Data {
  isValid: boolean
  statusId: number | null
  description: string | null
  name: string | null
  isSaving: boolean
  errorSchema: object
  customData: object
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FieldViewLocationDetailsEdit',
  props: {
    statusId: { default: null },
    description: { default: null },
    name: { default: '' },
    locationId: { default: '' },
    rootSchema: { default: null },
    customData: { default: null }
  },
  emits: ['dismiss', 'save'],
  setup(__props: any, { emit: emits }) {

const props = __props as { statusId: number | null, description: string | null, name: string, locationId: number | string, rootSchema: LocationTypeCustomFieldsSchema | null, customData: object | null }

const { data: statuses } = useGetLocationStatusesQuery()
const statusesForAutocomplete = computed(() => {
  return statuses.value?.map((s) => ({
    text: s.name,
    value: s.id,
    color: s.hexCode,
  }))
})

const store = useStore()

const getCurrentUserContext = computed<CurrentUserContext>(
  () => store.getters['permissions/getContext']
)


const data: Data = reactive({
  isValid: true,
  statusId: null,
  description: null,
  name: '',
  isSaving: false,
  errorSchema: {},
  customData: {},
})

const statusColor = computed(() => {
  return statuses.value?.find((s) => s.id === data.statusId)?.hexCode
})

const hasChanges = computed(() => {
  return (
    data.statusId !== props.statusId ||
    data.description !== props.description ||
    !isEqual(data.customData, props.customData)
  )
})

const schema = computed(() => {
  return props.rootSchema?.schema
})

const uiSchema = computed(() => {
  return props.rootSchema?.uiSchema
})

const handleUpdateStatus = (value) => {
  data.statusId = value
}

const handleUpdateDescription = (value) => {
  data.description = value
}

const handleUpdateCustomData = (value) => {
  if (customFieldsAreValid()) {
    data.customData = value
  }
}

const customFieldsAreValid = () => {
  const resolvedSchema = contextualizedFormResolver.value.retrieveSchema(
    schema,
    schema,
    data.customData
  )
  const resolvedData = resolveFormDataForValidation(data.customData)

  const errorSchema = validateFormData(resolvedSchema.value, resolvedData)
  data.errorSchema = errorSchema

  return isEmpty(errorSchema)
}

const contextualizedFormResolver = computed(() => {
  const currentUserContext = getCurrentUserContext.value
  const currentUser = {
    id: currentUserContext?.userSid,
    name: currentUserContext?.name,
  }
  const context = new FormContext({
    hub: null,
    currentUser,
    additionalContext: '',
  })
  return new ContextualizedFormResolver(context)
})

onMounted(() => {
  data.statusId = props.statusId
  data.description = props.description
  data.name = props.name
  data.customData = cloneDeep(props.customData) ?? {}
})



const handleDismiss = () => {
  emits('dismiss')
}
const handleSave = async () => {
  data.isSaving = true
  await updateLocationDetails(
    props.locationId,
    data.statusId,
    data.description,
    data.customData
  )
  data.isSaving = false
  emits('save')
}

return { __sfc: true,statuses, statusesForAutocomplete, store, getCurrentUserContext, props, data, statusColor, hasChanges, schema, uiSchema, handleUpdateStatus, handleUpdateDescription, handleUpdateCustomData, customFieldsAreValid, contextualizedFormResolver, emits, handleDismiss, handleSave, FormLayout }
}

})